import dayjs from 'dayjs';

import type {
	PendingCutBar,
	PendingCutMaterialGroup,
	PendingCutReservation,
} from '../../../../redux/api/pending-cuts';
import type { LocationLabelFormatter } from '../../../../utils/locationLabel';

/**
 * One row per reservation, with the bar's cells spanning all of its
 * reservations, so the screen table and the printout read bar by bar.
 */
export type PendingCutRow =
	| { kind: 'section'; key: string; group: PendingCutMaterialGroup }
	| {
			kind: 'reservation';
			key: string;
			bar: PendingCutBar;
			reservation: PendingCutReservation;
			/** Rows the bar's cells span on its first reservation; 0 on the others. */
			barSpan: number;
	  };

export const toRows = (groups: PendingCutMaterialGroup[]): PendingCutRow[] =>
	groups.flatMap((group) => [
		{
			kind: 'section' as const,
			key: `s:${group.material_id}:${group.bars[0]?.id ?? 'empty'}`,
			group,
		},
		...group.bars.flatMap((bar) =>
			bar.reservations.map((reservation, index) => ({
				kind: 'reservation' as const,
				key: reservation.id,
				bar,
				reservation,
				barSpan: index === 0 ? bar.reservations.length : 0,
			}))
		),
	]);

export const reservationCount = (groups: PendingCutMaterialGroup[]) =>
	groups.reduce(
		(total, group) =>
			total +
			group.bars.reduce((sum, bar) => sum + bar.reservations.length, 0),
		0
	);

/**
 * Pack bars into pages of about `pageSize` table rows without splitting a bar.
 * A material section header is repeated at the top of each page that continues
 * that material, so `toRows` can set `barSpan` correctly per page.
 */
export const paginateGroupsByBars = (
	groups: PendingCutMaterialGroup[],
	pageSize: number
): PendingCutMaterialGroup[][] => {
	if (pageSize < 1) return groups.length ? [groups] : [];

	const pages: PendingCutMaterialGroup[][] = [];
	let page: PendingCutMaterialGroup[] = [];
	let rowsOnPage = 0;
	const materialsOnPriorPages = new Set<string>();

	const flush = () => {
		if (!page.length) return;
		for (const slice of page) {
			materialsOnPriorPages.add(slice.material_id);
		}
		pages.push(page);
		page = [];
		rowsOnPage = 0;
	};

	const lastGroupOnPage = () => page[page.length - 1];

	for (const group of groups) {
		const totalBars = group.total_bars ?? group.bars.length;
		for (const bar of group.bars) {
			const needsSection =
				!page.length || lastGroupOnPage()?.material_id !== group.material_id;
			const cost = (needsSection ? 1 : 0) + bar.reservations.length;

			if (rowsOnPage > 0 && rowsOnPage + cost > pageSize) {
				flush();
			}

			const sectionNeeded =
				!page.length || lastGroupOnPage()?.material_id !== group.material_id;
			if (sectionNeeded) {
				page.push({
					material_id: group.material_id,
					material_name: group.material_name,
					pending_pieces: group.pending_pieces,
					total_bars: totalBars,
					continued: materialsOnPriorPages.has(group.material_id),
					bars: [bar],
				});
				rowsOnPage += 1 + bar.reservations.length;
			} else {
				lastGroupOnPage().bars.push(bar);
				rowsOnPage += bar.reservations.length;
			}
		}
	}
	flush();
	return pages;
};

export const inches = (value: string | null | undefined) =>
	value == null ? '—' : `${value}"`;

export const piecesDetail = (r: PendingCutReservation) =>
	[
		`of ${r.cut_quantity} planned`,
		r.actual_cut_quantity > 0 && `${r.actual_cut_quantity} already cut`,
		r.scrap_quantity > 0 && `incl. ${r.scrap_quantity} scrap`,
	]
		.filter(Boolean)
		.join(' · ');

export const jobText = (r: PendingCutReservation) => {
	const oli = r.order_line_item;
	if (!oli) return r.reserved_for_description || 'No job';
	return [oli.order_name, oli.name].filter(Boolean).join(' · ');
};

export const dueText = (r: PendingCutReservation) => {
	const due = r.order_line_item?.due_date;
	return due ? dayjs(due).format('M/D/YY') : '—';
};

export const statusText = (r: PendingCutReservation) =>
	[r.status, r.is_swiss && 'Swiss'].filter(Boolean).join(' · ');

const plural = (n: number, one: string, many: string) =>
	`${n} ${n === 1 ? one : many}`;

export const groupCounts = (group: PendingCutMaterialGroup) =>
	`${plural(group.total_bars ?? group.bars.length, 'bar', 'bars')} · ${plural(
		group.pending_pieces,
		'pc',
		'pcs'
	)}`;

/** Rack/slot alone when one building is selected; otherwise prefix the building. */
export const barLocationText = (
	bar: PendingCutBar,
	formatLocation: LocationLabelFormatter,
	showBuilding: boolean
): string | null => {
	const location = bar.location ? formatLocation(bar.location) : null;
	const building = showBuilding ? bar.building?.name : null;
	if (building && location) return `${building} · ${location}`;
	return building || location;
};
